import { useDetalheAuditoria } from "../../lib/auditoria/hooks";
import { formatarDataHoraISO } from "../../lib/formato/data";
import { ErroHttp } from "../../lib/http/erros";

/*
 * Detalhe de um registro da trilha de auditoria (FE-5 CP-C — LEITURA).
 *
 * Consome `useDetalheAuditoria(empresaId, registroId)` e EXIBE os metadados +
 * os snapshots `antes`/`depois`. Estes snapshots JÁ vêm MASCARADOS pelo backend
 * (PII/segredos redigidos) — esta camada NUNCA tenta desmascarar.
 *
 * DISCIPLINA DE DADO SENSÍVEL (regra dura do projeto):
 *  - NÃO logar: nenhum `console.*` aqui (o conteúdo, mesmo mascarado, não vai
 *    para o console).
 *  - NÃO persistir: nada de localStorage/cookie/sessionStorage — só `useState`
 *    efêmero de tela (e nem isso é necessário; é tudo derivado da query).
 *  - APENAS EXIBIR: renderizamos chave→valor; valores complexos (objeto/array)
 *    via `JSON.stringify(valor, null, 2)` dentro de `<pre>`.
 *
 * `null` em `antes`/`depois` → "sem dados" (ex.: criação não tem `antes`,
 * exclusão não tem `depois`).
 *
 * ESTADOS: loading/erro espelham as demais telas (503 → indisponível tratado pela
 * mensagem do `ErroHttp`; demais reusam a mensagem estável).
 */

const SEM_VALOR = "—";

interface DetalheAuditoriaProps {
  empresaId: string;
  registroId: string;
}

/** Renderiza um valor opaco de snapshot de forma legível (sem desmascarar). */
function exibirValor(valor: unknown): string {
  if (valor === null || valor === undefined) {
    return SEM_VALOR;
  }
  if (
    typeof valor === "string" ||
    typeof valor === "number" ||
    typeof valor === "boolean"
  ) {
    return String(valor);
  }
  // Objeto/array: exibe a estrutura crua (já mascarada) de forma legível.
  return JSON.stringify(valor, null, 2);
}

interface BlocoSnapshotProps {
  titulo: string;
  dados: Record<string, unknown> | null;
}

/** Um bloco `antes`/`depois`: lista de chave→valor, ou "sem dados" se `null`. */
function BlocoSnapshot({ titulo, dados }: BlocoSnapshotProps): JSX.Element {
  return (
    <div className="flex flex-col gap-1">
      <h3 className="font-subtitulo">{titulo}</h3>
      {dados === null ? (
        <p className="font-corpo text-base text-off-black">sem dados</p>
      ) : (
        <dl className="flex flex-col gap-1 font-corpo text-base text-off-black">
          {Object.entries(dados).map(([chave, valor]) => (
            <div key={chave} className="flex flex-col gap-1">
              <dt className="font-subtitulo">{chave}</dt>
              <dd>
                <pre className="whitespace-pre-wrap break-words font-corpo">
                  {exibirValor(valor)}
                </pre>
              </dd>
            </div>
          ))}
        </dl>
      )}
    </div>
  );
}

export function DetalheAuditoria({
  empresaId,
  registroId,
}: DetalheAuditoriaProps): JSX.Element {
  const consulta = useDetalheAuditoria(empresaId, registroId);

  if (consulta.isPending) {
    return (
      <p className="font-corpo text-base text-off-black">
        Carregando detalhe…
      </p>
    );
  }

  if (consulta.isError) {
    const mensagem =
      consulta.error instanceof ErroHttp
        ? consulta.error.message
        : "Não foi possível carregar o detalhe.";
    return (
      <p role="alert" className="font-corpo text-base text-flame">
        {mensagem}
      </p>
    );
  }

  const detalhe = consulta.data;

  return (
    <div className="flex flex-col gap-4 rounded-md border border-platinum bg-snow p-4 font-corpo text-base text-off-black">
      <h2 className="font-subtitulo text-lg">Detalhe do registro</h2>

      <dl className="flex flex-col gap-1">
        <div className="flex gap-2">
          <dt className="font-subtitulo">Data/hora</dt>
          <dd>{formatarDataHoraISO(detalhe.ts)}</dd>
        </div>
        <div className="flex gap-2">
          <dt className="font-subtitulo">Ação</dt>
          <dd>{detalhe.acao}</dd>
        </div>
        <div className="flex gap-2">
          <dt className="font-subtitulo">Recurso</dt>
          <dd>{detalhe.recurso}</dd>
        </div>
        <div className="flex gap-2">
          <dt className="font-subtitulo">Usuário</dt>
          <dd>{detalhe.usuario_id ?? SEM_VALOR}</dd>
        </div>
        <div className="flex gap-2">
          <dt className="font-subtitulo">Correlação</dt>
          <dd>{detalhe.correlation_id ?? SEM_VALOR}</dd>
        </div>
      </dl>

      <BlocoSnapshot titulo="Antes" dados={detalhe.antes} />
      <BlocoSnapshot titulo="Depois" dados={detalhe.depois} />
    </div>
  );
}
